import React, { useRef, useState } from 'react';
import { usePost } from 'hooks/usePost';
import Tooltip from 'component/Custom/tooltip/Tooltip';
import { chat } from 'hooks/endpoints';
import styles from './ResponseReactions.module.scss';
import toast from 'react-hot-toast';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import Loader from 'component/atom/loader/Loader';
import isPauseSvg from './assets/pause 2 1.gif';
import { API_URL } from 'constants';
import { useAudioPlayer } from './hook/useAudioPlayer';

const audioCache = new Map();

const ResponseReactions = ({
  responseId,
  botId,
  chatData,
  setGuestChat,
  guestChat,
  settings,
  step,
}) => {
  const audioRef = useRef(new Audio());
  const { play, pause, getCurrentStepId } = useAudioPlayer();
  const [rated, setRated] = useState();
  const { mutate: RateChatResponse } = usePost();
  const playVoice = async () => {
    const stepId = chatData.stepId;

    // Check if currently playing this same voice
    if (chatData.isPlaying) {
      pause();
      updatePlayingState(stepId, false);
      return;
    }

    // Reset all other messages
    setGuestChat((prev) => ({
      ...prev,
      messages: prev.messages.map((msg) => ({
        ...msg,
        isPlaying: false,
        isVoiceLoading: false,
      })),
    }));

    // Play from cache if available
    if (audioCache.has(stepId)) {
      const url = audioCache.get(stepId);
      const state = play(url, stepId, () => updatePlayingState(stepId, false));
      updatePlayingState(stepId, state === 'playing');
      return;
    }

    updatePlayingState(stepId, false, true);

    try {
      const response = await fetch(
        `${API_URL}/api/ChatBot/TextToSpeech/${responseId}/${botId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) throw new Error('Failed to fetch voice data');

      const data = await response.arrayBuffer();
      if (!data) return;

      const audioBlob = new Blob([data], { type: 'audio/wav' });
      if (audioBlob.size === 0) return;

      const audioUrl = URL.createObjectURL(audioBlob);
      audioCache.set(stepId, audioUrl);

      const state = play(audioUrl, stepId, () =>
        updatePlayingState(stepId, false)
      );
      updatePlayingState(stepId, state === 'playing');
    } catch (error) {
      updatePlayingState(stepId, false);
      toast.error('Failed to play voice');
    }
  };
  const updatePlayingState = (stepId, isPlaying, isLoading = false) => {
    setGuestChat((prev) => ({
      ...prev,
      messages: prev.messages.map((msg) =>
        msg.stepId === stepId
          ? { ...msg, isPlaying, isVoiceLoading: isLoading }
          : { ...msg, isPlaying: false, isVoiceLoading: false }
      ),
    }));
  };
  const handleRateChatResponse = (rating) => {
    RateChatResponse(
      {
        url: chat.rateChatResponse,
        data: {
          detailChatId: responseId,
          rating: rating,
        },
      },
      {
        onSuccess: () => {
          setRated({
            like: rating === 5,
            dislike: rating === 1,
          });
          toast.success(
            `You ${rating === 5 ? 'liked' : 'disliked'} the response.`
          );
        },
        onError: () => {
          toast.error('Failed to rate the response.');
        },
      }
    );
  };
  const handleCopy = () => {
    if (!chatData?.content) return;
    setRated((prev) => ({
      ...prev,
      copy: true,
    }));
    const chatMessages = chatData?.content
      .map((item) => {
        if (!item?.value) return;
        let textToCopy = '';
        if (typeof item?.value === 'string') {
          textToCopy = item?.value.replace(/<\/?[^>]+(>|$)/g, '');
        } else if (Array.isArray(item?.value)) {
          const questionText = item?.value.find(
            (subItem) => subItem.questionText
          );
          if (questionText) {
            textToCopy = questionText?.questionText.replace(
              /<\/?[^>]+(>|$)/g,
              ''
            );
          }
        }

        return textToCopy;
      })
      .filter(Boolean)
      .join('\n');

    if (chatMessages) {
      navigator.clipboard.writeText(chatMessages);
      toast.success('Copied');
    }
    setTimeout(() => {
      setRated((prev) => ({
        ...prev,
        copy: false,
      }));
    }, [2000]);
  };

  const downloadChatConversation = async (apiBase) => {
    const url = `${apiBase.replace(
      /\/+$/,
      ''
    )}/Chat/DownloadChat/${responseId}`;

    const res = await fetch(url, { method: 'GET' });

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(
        `DownloadChatConversation ${res.status}: ${text || res.statusText}`
      );
    }

    return res.blob();
  };

  const handleDownloadChatDetail = async () => {
    const blob = await downloadChatConversation(API_URL);

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'chat_conversation.docx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  let reactions = [
    {
      id: 1,
      type: 'copySvg',
      alt: 'Copy',
      onClick: handleCopy,
      color: rated?.copy ? '#0844E1' : '#595959',
    },
    // {
    //   id: 2,
    //   type: 'refetchSvg',
    //   alt: 'Refresh',
    //   onClick: () => {},
    //   color: '#595959',
    // },
    {
      id: 3,
      type: 'likeSvg',
      alt: 'Like',
      onClick: () => handleRateChatResponse(5),
      color: rated?.like ? '#007BFF' : '#595959',
    },
    {
      id: 4,
      type: 'dislikeSvg',
      alt: 'Dislike',
      onClick: () => handleRateChatResponse(1),
      color: rated?.dislike ? '#FF0000' : '#595959',
    },
    {
      id: 5,
      type: 'download2',
      alt: 'Download',
      onClick: () => handleDownloadChatDetail(),
      color: '#595959',
    },
  ];
  if (settings?.readAloud) {
    reactions.unshift({
      id: 5,
      type: 'speakerSvg',
      alt: 'Speaker',
      onClick: playVoice,
      isVoiceLoading: chatData.isVoiceLoading,
      isPlaying: chatData.isPlaying,
      color: '#595959',
    });
  }
  if (step.type === 'Form') {
    reactions = reactions.filter(
      (reaction) => reaction.id !== 5 && reaction.id !== 1
    );
  }

  return (
    <div className={styles['chat-sub-actions']}>
      {reactions.map((item, index) => (
        <Tooltip content={item.alt} placement='bottom' key={index}>
          {item.isVoiceLoading ? (
            <div className={styles.loader}>
              <Loader
                style={{ width: '17px', height: '17px' }}
                isComponent
                classname={styles.loaderClass}
              />
            </div>
          ) : item.isPlaying ? (
            <img src={isPauseSvg} alt='Pause' onClick={item.onClick} />
          ) : (
            <SvgWithBranding
              type={item.type}
              alt={item.alt}
              onClick={item.onClick}
              className={`${styles.svg} ${item.className ?? ''} ${
                item?.type === 'download2' && styles['download-icon']
              }`}
              color={item.color}
            />
          )}
        </Tooltip>
      ))}
      {audioRef ? <audio ref={audioRef} /> : null}
    </div>
  );
};

export default ResponseReactions;
